import type { ReactElement, ReactNode } from 'react';
import type { AdminCapability } from '@kopechka/contracts';
import { useSession } from './session-context.js';

export interface CanProps {
  readonly capability: AdminCapability;
  readonly children: ReactNode;
  readonly fallback?: ReactNode;
}

/**
 * Conditionally render `children` when the current principal has `capability`.
 * Renders nothing by default when the check fails — the server remains the
 * source of authority, this is strictly a UX affordance so deny-states never
 * flash to the operator. Pass `fallback` to render a disabled stand-in.
 */
export function Can({ capability, children, fallback = null }: CanProps): ReactElement {
  const session = useSession();
  if (session.has(capability)) return <>{children}</>;
  return <>{fallback}</>;
}

/**
 * Hook variant returning a simple boolean. Prefer `<Can>` for markup; use
 * this when a non-JSX decision is needed (e.g. computing nav item list).
 */
export function useCan(capability: AdminCapability): boolean {
  const session = useSession();
  return session.has(capability);
}
